import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { TASK_STATUSES } from "@/lib/roles";
import { fmtDate, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const STATUS_BAR = { NOT_STARTED: "bg-slate-400 dark:bg-slate-600", IN_PROGRESS: "bg-amber-500", COMPLETED: "bg-emerald-500", DELAYED: "bg-rose-500" };

export default function Schedule() {
  const { projectId, isProjectManager } = useProjectCtx();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogTask, setDialogTask] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/schedule-tasks`);
      setTasks(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const { minDate, maxDate } = useMemo(() => {
    const dates = tasks.flatMap((t) => [t.start_date, t.end_date]).filter(Boolean).map((d) => dayjs(d));
    if (dates.length === 0) return { minDate: dayjs(), maxDate: dayjs().add(30, "day") };
    return { minDate: dates.reduce((a, b) => (a.isBefore(b) ? a : b)), maxDate: dates.reduce((a, b) => (a.isAfter(b) ? a : b)) };
  }, [tasks]);
  const totalDays = Math.max(maxDate.diff(minDate, "day"), 1);

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/schedule-tasks/${id}`);
      toast.success("Task deleted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="schedule-page">
      <PageHeader
        title="Schedule / Gantt"
        description="Top-level milestone tracker ordered by sequence."
        action={isProjectManager && <Button size="sm" onClick={() => setDialogTask({})} data-testid="add-schedule-task-button"><Plus className="h-3.5 w-3.5" /> Add task</Button>}
      />

      <div className="rounded-xl border border-border bg-card p-4" data-testid="gantt-chart-container">
        {!loading && tasks.length === 0 ? (
          <EmptyState title="No schedule tasks yet" testId="schedule-empty-state" />
        ) : (
          <div className="space-y-3">
            {tasks.map((t) => {
              const dep = tasks.find((x) => x.id === t.depends_on_id);
              const start = t.start_date ? dayjs(t.start_date) : null;
              const end = t.end_date ? dayjs(t.end_date) : null;
              const left = start ? (start.diff(minDate, "day") / totalDays) * 100 : 0;
              const width = start && end ? Math.max(((end.diff(start, "day") || 1) / totalDays) * 100, 1.5) : 2;
              return (
                <div key={t.id} data-testid={`gantt-row-${t.id}`} className="group">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground">{t.name}</span>
                      <StatusPill value={t.status} />
                      {dep && <span className="text-[11px] text-muted-foreground">↳ depends on: {dep.name}</span>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      {isProjectManager && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setDialogTask(t)} data-testid={`edit-schedule-task-btn-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`delete-schedule-task-btn-${t.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete task?</AlertDialogTitle><AlertDialogDescription>{t.name}</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(t.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="relative h-5 rounded bg-muted">
                    <div
                      className={`absolute inset-y-0 rounded ${STATUS_BAR[t.status] || "bg-slate-400 dark:bg-slate-600"}`}
                      style={{ left: `${left}%`, width: `${width}%` }}
                      title={`${fmtDate(t.start_date)} → ${fmtDate(t.end_date)}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {dialogTask && <TaskDialog projectId={projectId} tasks={tasks} initial={dialogTask} onClose={() => setDialogTask(null)} onSaved={load} />}
    </div>
  );
}

function TaskDialog({ projectId, tasks, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial.id);
  const [form, setForm] = useState({
    name: initial.name || "", responsibility: initial.responsibility || "", status: initial.status || "NOT_STARTED",
    sort_order: initial.sort_order ?? 0, start_date: initial.start_date || "", end_date: initial.end_date || "",
    actual_start_date: initial.actual_start_date || "", actual_end_date: initial.actual_end_date || "",
    progress_pct: initial.progress_pct ?? 0, depends_on_id: initial.depends_on_id || "none",
  });
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async () => {
    try {
      const payload = {
        ...form, sort_order: Number(form.sort_order) || 0, progress_pct: Number(form.progress_pct) || 0,
        start_date: form.start_date || null, end_date: form.end_date || null,
        actual_start_date: form.actual_start_date || null, actual_end_date: form.actual_end_date || null,
        depends_on_id: form.depends_on_id === "none" ? null : form.depends_on_id,
      };
      if (isEdit) await api.patch(`/projects/${projectId}/schedule-tasks/${initial.id}`, payload);
      else await api.post(`/projects/${projectId}/schedule-tasks`, payload);
      toast.success(isEdit ? "Task updated" : "Task created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="schedule-task-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit task" : "New schedule task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="task-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Responsibility</Label><Input data-testid="task-responsibility-input" value={form.responsibility} onChange={set("responsibility")} className="mt-1.5" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="task-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" data-testid="task-start-date-input" value={form.start_date} onChange={set("start_date")} className="mt-1.5" /></div>
            <div><Label>End date</Label><Input type="date" data-testid="task-end-date-input" value={form.end_date} onChange={set("end_date")} className="mt-1.5" /></div>
            <div><Label>Actual start</Label><Input type="date" data-testid="task-actual-start-input" value={form.actual_start_date} onChange={set("actual_start_date")} className="mt-1.5" /></div>
            <div><Label>Actual end</Label><Input type="date" data-testid="task-actual-end-input" value={form.actual_end_date} onChange={set("actual_end_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Progress % (0–1)</Label><Input type="number" step="0.01" data-testid="task-progress-input" value={form.progress_pct} onChange={set("progress_pct")} className="mt-1.5" /></div>
            <div>
              <Label>Depends on</Label>
              <Select value={form.depends_on_id} onValueChange={(v) => setForm({ ...form, depends_on_id: v })}>
                <SelectTrigger data-testid="task-depends-on-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {tasks.filter((t) => t.id !== initial.id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter><Button disabled={!form.name} onClick={submit} data-testid="task-submit-button">{isEdit ? "Save" : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
